/** Stable identity used by reviewed CurriculumToolBinding records. */
export const INTERMOLECULAR_FORCES_TOOL_IDENTITY = {
  id: 'intermolecular-forces',
  toolKey: 'chemistry-intermolecular-forces',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const INTERMOLECULAR_FORCES_START_YEAR = 11;

export const IMF_REVIEW_STATUS = 'pending-teaching-review' as const;

export const IMF_DATA_SOURCES = [
  'CRC Handbook of Chemistry and Physics, 97th edition (2016) — normal boiling points',
  'NIST Chemistry WebBook, SRD 69 — phase-change data',
  'IUPAC standard atomic weights — molar masses',
] as const;

export const IMF_CONDITIONS =
  'Normal boiling point: the temperature at which the liquid boils at 101.325 kPa (1 atm). Values rounded to 0.1 °C.';

export type ImfType = 'dispersion' | 'dipole-dipole' | 'hydrogen-bonding';

export const IMF_LABELS: Record<ImfType, string> = {
  dispersion: 'Dispersion forces',
  'dipole-dipole': 'Dipole–dipole forces',
  'hydrogen-bonding': 'Hydrogen bonding',
};

/** Ordered from weakest type to strongest type, for a molecule of similar size. */
export const IMF_TYPE_ORDER: ImfType[] = ['dispersion', 'dipole-dipole', 'hydrogen-bonding'];

export interface Substance {
  id: string;
  name: string;
  /** Formula with Unicode subscripts. */
  formula: string;
  molarMass: number;
  polar: boolean;
  /** True only when H is covalently bonded to N, O or F in the molecule. */
  hBondDonor: boolean;
  /** Normal boiling point in °C, or null when the substance does not boil at 101.325 kPa. */
  boilingPointC: number | null;
  note: string;
}

export const SUBSTANCES: Substance[] = [
  { id: 'ne', name: 'Neon', formula: 'Ne', molarMass: 20.18, polar: false, hBondDonor: false, boilingPointC: -246.1, note: 'A single atom with only 10 electrons, so its dispersion forces are very weak.' },
  { id: 'ar', name: 'Argon', formula: 'Ar', molarMass: 39.95, polar: false, hBondDonor: false, boilingPointC: -185.8, note: 'More electrons than neon, so a larger, more polarisable electron cloud.' },
  { id: 'kr', name: 'Krypton', formula: 'Kr', molarMass: 83.8, polar: false, hBondDonor: false, boilingPointC: -153.2, note: 'The largest noble gas here, so the strongest dispersion forces of the three.' },
  { id: 'ch4', name: 'Methane', formula: 'CH₄', molarMass: 16.04, polar: false, hBondDonor: false, boilingPointC: -161.5, note: 'Symmetrical tetrahedral molecule. The small C–H bond dipoles cancel.' },
  { id: 'c2h6', name: 'Ethane', formula: 'C₂H₆', molarMass: 30.07, polar: false, hBondDonor: false, boilingPointC: -88.6, note: 'One more carbon than methane gives a larger surface for dispersion forces.' },
  { id: 'c3h8', name: 'Propane', formula: 'C₃H₈', molarMass: 44.1, polar: false, hBondDonor: false, boilingPointC: -42.1, note: 'Non-polar, but its longer chain gives stronger dispersion forces than small polar molecules such as HCl.' },
  { id: 'c4h10', name: 'Butane', formula: 'C₄H₁₀', molarMass: 58.12, polar: false, hBondDonor: false, boilingPointC: -0.5, note: 'Each extra CH₂ unit raises the boiling point by about 35–75 °C in this short series.' },
  { id: 'c5h12', name: 'Pentane', formula: 'C₅H₁₂', molarMass: 72.15, polar: false, hBondDonor: false, boilingPointC: 36.1, note: 'The first straight-chain alkane here that is liquid at room temperature.' },
  { id: 'hf', name: 'Hydrogen fluoride', formula: 'HF', molarMass: 20.01, polar: true, hBondDonor: true, boilingPointC: 19.5, note: 'H bonded to F forms strong hydrogen bonds, so HF boils far above HCl.' },
  { id: 'hcl', name: 'Hydrogen chloride', formula: 'HCl', molarMass: 36.46, polar: true, hBondDonor: false, boilingPointC: -85.1, note: 'Polar, but Cl is not small and electronegative enough for hydrogen bonding.' },
  { id: 'hbr', name: 'Hydrogen bromide', formula: 'HBr', molarMass: 80.91, polar: true, hBondDonor: false, boilingPointC: -66.4, note: 'Less polar than HCl, but its larger electron cloud gives stronger dispersion forces, so it boils higher.' },
  { id: 'h2o', name: 'Water', formula: 'H₂O', molarMass: 18.02, polar: true, hBondDonor: true, boilingPointC: 100, note: 'Each molecule can form up to four hydrogen bonds, which explains its very high boiling point for its size.' },
  { id: 'h2s', name: 'Hydrogen sulfide', formula: 'H₂S', molarMass: 34.08, polar: true, hBondDonor: false, boilingPointC: -60.3, note: 'Heavier than water, but S is not electronegative enough for hydrogen bonding.' },
  { id: 'nh3', name: 'Ammonia', formula: 'NH₃', molarMass: 17.03, polar: true, hBondDonor: true, boilingPointC: -33.3, note: 'H bonded to N gives hydrogen bonding, so it boils far above PH₃.' },
  { id: 'ph3', name: 'Phosphine', formula: 'PH₃', molarMass: 34.0, polar: true, hBondDonor: false, boilingPointC: -87.8, note: 'Only weakly polar. P is not electronegative enough for hydrogen bonding.' },
  { id: 'ch3oh', name: 'Methanol', formula: 'CH₃OH', molarMass: 32.04, polar: true, hBondDonor: true, boilingPointC: 64.7, note: 'The O–H group forms hydrogen bonds.' },
  { id: 'c2h5oh', name: 'Ethanol', formula: 'CH₃CH₂OH', molarMass: 46.07, polar: true, hBondDonor: true, boilingPointC: 78.3, note: 'O–H hydrogen bonding plus a longer carbon chain than methanol.' },
  { id: 'ch3och3', name: 'Dimethyl ether', formula: 'CH₃OCH₃', molarMass: 46.07, polar: true, hBondDonor: false, boilingPointC: -24.8, note: 'Same formula as ethanol (C₂H₆O), but every H is bonded to C, so its molecules cannot hydrogen bond to each other.' },
  { id: 'co2', name: 'Carbon dioxide', formula: 'CO₂', molarMass: 44.01, polar: false, hBondDonor: false, boilingPointC: null, note: 'Linear molecule, so the two C=O bond dipoles cancel. It sublimes at −78.5 °C at 101.325 kPa and has no normal boiling point.' },
];

/** Formats a temperature with a true minus sign, for example −85.1 °C. */
export function formatCelsius(value: number): string {
  return `${value < 0 ? '−' : ''}${Math.abs(value)} °C`;
}

export function substanceById(id: string): Substance | undefined {
  return SUBSTANCES.find((substance) => substance.id === id);
}

/** IMFs present between molecules of one pure substance. Dispersion is always present. */
export function imfsPresent(substance: Substance): ImfType[] {
  const present: ImfType[] = ['dispersion'];
  if (substance.polar) present.push('dipole-dipole');
  if (substance.hBondDonor) present.push('hydrogen-bonding');
  return present;
}

/** The strongest type of IMF present (not always the largest contribution: see HBr). */
export function strongestImf(substance: Substance): ImfType {
  const present = imfsPresent(substance);
  return present[present.length - 1];
}

// ---------------------------------------------------------------------------------------------
// Activity 1: identify the IMFs present
// ---------------------------------------------------------------------------------------------

export interface IdentifyResult {
  correct: boolean;
  feedback: string[];
}

export function checkIdentification(substance: Substance, selected: ImfType[]): IdentifyResult {
  const expected = imfsPresent(substance);
  const feedback: string[] = [];
  if (!selected.includes('dispersion')) {
    feedback.push('Every molecule has dispersion forces. Moving electrons create temporary dipoles in all particles.');
  }
  if (substance.polar && !selected.includes('dipole-dipole')) {
    feedback.push(`${substance.formula} is polar, so its permanent dipoles attract each other (dipole–dipole forces).`);
  }
  if (!substance.polar && selected.includes('dipole-dipole')) {
    feedback.push(
      substance.id === 'co2'
        ? 'CO₂ has polar C=O bonds, but the molecule is linear, so the bond dipoles cancel. The molecule is non-polar.'
        : `${substance.formula} is non-polar, so it has no permanent dipole.`,
    );
  }
  if (substance.hBondDonor && !selected.includes('hydrogen-bonding')) {
    feedback.push(`${substance.formula} has H bonded directly to N, O or F, so it forms hydrogen bonds.`);
  }
  if (!substance.hBondDonor && selected.includes('hydrogen-bonding')) {
    feedback.push(
      substance.id === 'ch3och3'
        ? 'Dimethyl ether contains O, but all its H atoms are bonded to C. Hydrogen bonding needs H bonded directly to N, O or F.'
        : `${substance.formula} has no H bonded to N, O or F, so it cannot form hydrogen bonds between its molecules.`,
    );
  }
  const correct =
    feedback.length === 0 &&
    expected.length === selected.length &&
    expected.every((type) => selected.includes(type));
  return {
    correct,
    feedback: correct
      ? [`Correct: ${expected.map((type) => IMF_LABELS[type].toLowerCase()).join(', ')}. ${substance.note}`]
      : feedback,
  };
}

// ---------------------------------------------------------------------------------------------
// Activity 2: rank by boiling point
// ---------------------------------------------------------------------------------------------

export interface RankingChallenge {
  id: string;
  title: string;
  focus: string;
  substanceIds: string[];
  explanation: string;
}

export const RANKING_CHALLENGES: RankingChallenge[] = [
  {
    id: 'alkane-chain',
    title: 'Alkane chain length',
    focus: 'Same type of force, growing molecules',
    substanceIds: ['c3h8', 'ch4', 'c4h10', 'c2h6'],
    explanation:
      'All four are non-polar, so only dispersion forces act. Longer chains have more electrons and more surface contact, so dispersion forces and boiling points rise with chain length.',
  },
  {
    id: 'noble-gases',
    title: 'Noble gases',
    focus: 'Single atoms of increasing size',
    substanceIds: ['kr', 'ne', 'ar'],
    explanation:
      'Single atoms have only dispersion forces. Larger atoms have larger, more easily distorted electron clouds, so Ne < Ar < Kr.',
  },
  {
    id: 'group-16-hydrides',
    title: 'Water and hydrogen sulfide',
    focus: 'The water anomaly',
    substanceIds: ['h2s', 'ch4', 'h2o'],
    explanation:
      'H₂S is heavier than water, so dispersion alone would predict that it boils higher. Water boils 160 °C higher because O–H…O hydrogen bonds are much stronger. S is not electronegative enough to hydrogen bond.',
  },
  {
    id: 'hydrogen-halides',
    title: 'Hydrogen halides',
    focus: 'HF versus HCl and HBr',
    substanceIds: ['hbr', 'hf', 'hcl'],
    explanation:
      'HF hydrogen bonds, so it boils highest despite being the lightest. Between HCl and HBr, HBr is less polar but has more electrons, so stronger dispersion forces make it boil higher.',
  },
  {
    id: 'isomers',
    title: 'Ethanol and dimethyl ether',
    focus: 'Same formula, different structure',
    substanceIds: ['ch3och3', 'c3h8', 'c2h5oh'],
    explanation:
      'Ethanol and dimethyl ether are isomers (both C₂H₆O, 46.07 g/mol). Ethanol has an O–H group and hydrogen bonds. Dimethyl ether is polar but has no O–H, so it relies on weaker dipole–dipole forces. Propane, of similar mass and non-polar, boils lowest.',
  },
  {
    id: 'group-15-hydrides',
    title: 'Ammonia and phosphine',
    focus: 'Hydrogen bonding through N–H',
    substanceIds: ['ph3', 'nh3', 'ch4'],
    explanation:
      'NH₃ is lighter than PH₃ but boils over 50 °C higher because N–H…N hydrogen bonds form. PH₃ is only weakly polar. Non-polar CH₄ boils lowest.',
  },
  {
    id: 'similar-mass',
    title: 'Similar molar mass, different forces',
    focus: 'About 30–34 g/mol each',
    substanceIds: ['ch3oh', 'h2s', 'c2h6'],
    explanation:
      'With similar sizes, the type of force decides. Ethane has only dispersion forces, H₂S adds dipole–dipole forces and methanol adds hydrogen bonding.',
  },
  {
    id: 'alcohols',
    title: 'Alcohols and an alkane',
    focus: 'Hydrogen bonding plus chain length',
    substanceIds: ['c2h5oh', 'c4h10', 'ch3oh'],
    explanation:
      'Butane is the heaviest but cannot hydrogen bond, so it boils lowest. Both alcohols hydrogen bond. Ethanol has the longer chain, so its extra dispersion forces make it boil higher than methanol.',
  },
  {
    id: 'size-beats-polarity',
    title: 'When size beats polarity',
    focus: 'Small polar versus larger non-polar',
    substanceIds: ['hcl', 'c3h8', 'ar', 'hf'],
    explanation:
      'Argon (dispersion only) boils lowest. HCl is polar, but propane is larger, and its stronger dispersion forces outweigh HCl’s dipole–dipole forces. HF hydrogen bonds and boils highest.',
  },
  {
    id: 'liquids-at-room-temperature',
    title: 'Liquids at room temperature',
    focus: 'Pentane, ethanol and water',
    substanceIds: ['h2o', 'c5h12', 'c2h5oh'],
    explanation:
      'Pentane is the heaviest but has only dispersion forces. Ethanol has one O–H group per molecule. Water can form up to four hydrogen bonds per molecule, so it boils highest.',
  },
];

export interface RankingResult {
  correct: boolean;
  /** Correct order, lowest boiling point first. */
  correctOrder: string[];
  feedback: string;
}

/** Correct order (lowest boiling point first). Throws when a case lacks a normal boiling point. */
export function sortByBoilingPoint(substanceIds: string[]): string[] {
  const substances = substanceIds.map((id) => {
    const substance = substanceById(id);
    if (!substance) throw new Error(`Unknown substance: ${id}`);
    if (substance.boilingPointC === null) {
      throw new Error(`${substance.formula} has no normal boiling point and cannot be ranked`);
    }
    return { id, boilingPointC: substance.boilingPointC };
  });
  return [...substances].sort((a, b) => a.boilingPointC - b.boilingPointC).map((entry) => entry.id);
}

/** Explains why `higher` boils above `lower`, using reviewed data only. */
export function pairReason(lower: Substance, higher: Substance): string {
  const lowerRank = IMF_TYPE_ORDER.indexOf(strongestImf(lower));
  const higherRank = IMF_TYPE_ORDER.indexOf(strongestImf(higher));
  if (higherRank > lowerRank) {
    return `${higher.formula} has ${IMF_LABELS[strongestImf(higher)].toLowerCase()}, which ${lower.formula} lacks, so more energy is needed to separate its molecules.`;
  }
  if (higher.molarMass > lower.molarMass) {
    return `${higher.formula} (${higher.molarMass} g/mol) has more electrons than ${lower.formula} (${lower.molarMass} g/mol), so its dispersion forces are stronger${
      higherRank < lowerRank ? ' and outweigh the other force type here' : ''
    }.`;
  }
  return `${higher.formula} boils higher than ${lower.formula}. ${higher.note}`;
}

/**
 * Checks a learner order (lowest boiling point first). Fails closed (returns null) when the order
 * is not a permutation of the challenge substances.
 */
export function checkRanking(challenge: RankingChallenge, order: string[]): RankingResult | null {
  const expectedSet = [...challenge.substanceIds].sort().join('|');
  if ([...order].sort().join('|') !== expectedSet) return null;
  const correctOrder = sortByBoilingPoint(challenge.substanceIds);
  const correct = correctOrder.every((id, index) => order[index] === id);
  if (correct) {
    return { correct, correctOrder, feedback: `Correct order. ${challenge.explanation}` };
  }
  // First adjacent pair the learner placed the wrong way round.
  for (let index = 0; index < order.length - 1; index += 1) {
    const placedLower = substanceById(order[index]);
    const placedHigher = substanceById(order[index + 1]);
    if (!placedLower || !placedHigher) continue;
    if ((placedLower.boilingPointC ?? 0) > (placedHigher.boilingPointC ?? 0)) {
      return {
        correct,
        correctOrder,
        feedback: `You ranked ${placedLower.formula} lower than ${placedHigher.formula}, but ${placedLower.formula} boils higher (${formatCelsius(placedLower.boilingPointC ?? 0)} vs ${formatCelsius(placedHigher.boilingPointC ?? 0)}). ${pairReason(placedHigher, placedLower)}`,
      };
    }
  }
  return { correct, correctOrder, feedback: challenge.explanation };
}

/** Boiling-point chart rows, lowest first. Substances without a normal boiling point are excluded. */
export function boilingPointRows(substanceIds?: string[]): Substance[] {
  const pool = substanceIds
    ? substanceIds.map(substanceById).filter((substance): substance is Substance => Boolean(substance))
    : SUBSTANCES;
  return pool
    .filter((substance) => substance.boilingPointC !== null)
    .sort((a, b) => (a.boilingPointC ?? 0) - (b.boilingPointC ?? 0));
}
